/**
 * `/` — Start.
 *
 * Built from the mockup's entry screen (`Pius Selections.dc.html`, the `isEntry` block), which is a
 * strong authoritative source and decides several things the written docs never covered.
 *
 * Eight bands, in order: marketing bar · hero · "were you sent a link?" · three value propositions ·
 * the account-parity section · the scope statement · the organizations band · dark call to action
 * and footer.
 *
 * The load-bearing idea, and the reason the parity table exists at all: **planning works fully
 * without an account**, and the page has to say so plainly rather than treating the anonymous path
 * as a lesser option (docs/engineering/11-screens.md).
 */

import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from '@tanstack/react-router';

import { setLanguage } from '../../i18n';
import { useAuth, useSignOut } from '../auth/useAuth';
import './landing.css';

interface ParityRowConfig {
  key: string;
  labelKey: string;
  withoutAccount: boolean;
  withAccount: boolean;
}

const PARITY_CONFIG: ParityRowConfig[] = [
  {
    key: 'create',
    labelKey: 'landing.row_create_liturgy',
    withoutAccount: true,
    withAccount: true,
  },
  {
    key: 'plan',
    labelKey: 'landing.row_plan_every_section',
    withoutAccount: true,
    withAccount: true,
  },
  {
    key: 'invite',
    labelKey: 'landing.row_invite_by_link',
    withoutAccount: true,
    withAccount: true,
  },
  {
    key: 'approve',
    labelKey: 'landing.row_approve_finalize',
    withoutAccount: true,
    withAccount: true,
  },
  {
    key: 'policy',
    labelKey: 'landing.row_configure_policy',
    withoutAccount: true,
    withAccount: true,
  },
  {
    key: 'exports',
    labelKey: 'landing.row_generate_exports',
    withoutAccount: true,
    withAccount: true,
  },
  {
    key: 'attachments',
    labelKey: 'landing.row_attachments',
    withoutAccount: true,
    withAccount: true,
  },
  {
    key: 'dashboard',
    labelKey: 'landing.row_dashboard_listing',
    withoutAccount: false,
    withAccount: true,
  },
  {
    key: 'return',
    labelKey: 'landing.row_return_without_saving',
    withoutAccount: false,
    withAccount: true,
  },
  {
    key: 'notifications',
    labelKey: 'landing.row_email_notifications',
    withoutAccount: false,
    withAccount: true,
  },
];

/**
 * PLACEHOLDER — illustrative mock data carried over from the mockup. **Not real.**
 *
 * These names and version numbers are invented. Once Templates exist (Stage 4) and Official
 * designation exists (Stage 9), this card must read the genuinely designated Official Templates
 * from the API, with their real published version numbers.
 *
 * Shipping invented version numbers to a parish would be a small lie about what the platform
 * already carries, so this must not survive to launch. Tracked in docs/engineering/14-roadmap.md.
 *
 * Contrast with PARITY above, which is not placeholder: those rows describe what a Capability
 * actually grants today, and they match the authorization kernel.
 */
const PLACEHOLDER_OFFICIAL_TEMPLATES: { name: string; version: number }[] = [
  { name: 'Order of Celebrating Matrimony', version: 7 },
  { name: 'Order of Christian Funerals', version: 5 },
  { name: 'Rite of Baptism for Children', version: 3 },
];

export function LandingScreen(): React.JSX.Element {
  const { t, i18n } = useTranslation('common');
  const { account, isSignedIn } = useAuth();
  const signOut = useSignOut();
  const navigate = useNavigate();

  /**
   * Accepts a pasted Capability Link and opens it.
   *
   * Only the path is used, never the pasted origin — following a host someone pasted in would make
   * this a redirector to anywhere. Redemption itself lands in Stage 3.
   */
  const openCapabilityLink = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const raw = new FormData(event.currentTarget).get('capability_link');
    if (typeof raw !== 'string' || raw.trim().length === 0) return;

    const trimmed = raw.trim();
    let path = trimmed;

    if (/^https?:\/\//i.test(trimmed)) {
      try {
        path = new URL(trimmed).pathname;
      } catch {
        return;
      }
    }

    if (!path.startsWith('/')) path = `/${path}`;
    void navigate({ to: path });
  };

  return (
    <div className="landing">
      {/* ── marketing bar ─────────────────────────────────────────────── */}
      <header className="landing-bar">
        <img className="landing-bar__mark" src="/brand/shield.png" alt="" />
        <span className="landing-bar__wordmark">
          PIUS <span className="landing-bar__wordmark-second">SELECTIONS</span>
        </span>

        <nav className="landing-bar__nav" aria-label="About Pius Selections">
          <a href="#how-it-works">{t('landing.nav_how_it_works')}</a>
          <a href="#for-parishes">{t('landing.nav_for_parishes')}</a>
          <a href="#organizations">{t('nav.templates')}</a>

          {isSignedIn ? (
            <>
              <Link className="landing-bar__signin" to="/liturgies">
                {t('landing.your_liturgies')}
              </Link>
              <span className="landing-bar__identity">{account?.display_name}</span>
              <button
                type="button"
                className="landing-bar__signin"
                onClick={() => signOut.mutate()}
              >
                {t('auth.signout', 'Sign out')}
              </button>
            </>
          ) : (
            <Link className="landing-bar__signin" to="/login">
              {t('auth.signin.title')}
            </Link>
          )}
        </nav>
      </header>

      {/* ── hero ──────────────────────────────────────────────────────── */}
      <section className="landing-hero">
        <img className="landing-hero__mark" src="/brand/shield.png" alt="" />
        <h1 className="landing-hero__title">{t('landing.hero_title')}</h1>
        <p className="landing-hero__lede">{t('landing.hero_lede')}</p>

        <div className="landing-hero__actions">
          <Link className="btn btn--primary" to="/liturgies/new">
            {t('landing.plan_liturgy')}
          </Link>
          {isSignedIn ? (
            <Link className="btn btn--plain" to="/liturgies">
              {t('landing.your_liturgies')}
            </Link>
          ) : (
            <Link className="btn btn--plain" to="/login">
              {t('auth.signin.title')}
            </Link>
          )}
        </div>

        <p className="landing-hero__note">{t('landing.no_account_required')}</p>
      </section>

      {/* ── capability link ───────────────────────────────────────────── */}
      <section className="landing-link" aria-labelledby="sent-a-link">
        <p className="landing-eyebrow" id="sent-a-link">
          {t('landing.link_eyebrow')}
        </p>

        <form className="landing-link__form" onSubmit={openCapabilityLink}>
          {/* Labelled for screen readers; the eyebrow above is the visible label. */}
          <label className="visually-hidden" htmlFor="capability-link">
            {t('landing.link_placeholder')}
          </label>
          <input
            id="capability-link"
            name="capability_link"
            type="text"
            placeholder="piusselections.com/c/…"
            autoComplete="off"
            spellCheck={false}
          />
          <button type="submit" className="btn btn--blue">
            {t('landing.link_open')}
          </button>
        </form>

        <p className="landing-link__note">{t('landing.link_note')}</p>
      </section>

      {/* ── value propositions ────────────────────────────────────────── */}
      <section className="landing-props" id="how-it-works">
        <article>
          <p className="landing-eyebrow">{t('landing.prop1_eyebrow')}</p>
          <h2 className="landing-props__title">{t('landing.prop1_title')}</h2>
          <p className="landing-props__body">{t('landing.prop1_body')}</p>
        </article>

        <article>
          <p className="landing-eyebrow">{t('landing.prop2_eyebrow')}</p>
          <h2 className="landing-props__title">{t('landing.prop2_title')}</h2>
          <p className="landing-props__body">{t('landing.prop2_body')}</p>
        </article>

        <article>
          <p className="landing-eyebrow">{t('landing.prop3_eyebrow')}</p>
          <h2 className="landing-props__title">{t('landing.prop3_title')}</h2>
          <p className="landing-props__body">{t('landing.prop3_body')}</p>
        </article>
      </section>

      {/* ── account parity ────────────────────────────────────────────── */}
      <section className="landing-parity" id="for-parishes" aria-labelledby="parity-heading">
        <div className="landing-parity__inner">
          <div>
            <p className="landing-eyebrow">{t('landing.parity_eyebrow')}</p>
            <h2 className="landing-parity__heading" id="parity-heading">
              {t('landing.parity_heading')}
            </h2>
            <p className="landing-parity__body">{t('landing.parity_body1')}</p>
            <p className="landing-parity__body">{t('landing.parity_body2')}</p>

            <div className="landing-parity__actions">
              <Link className="btn btn--primary" to="/liturgies/new">
                {t('landing.start_without_account')}
              </Link>
              <Link className="btn btn--plain" to="/register">
                {t('landing.signin_or_create')}
              </Link>
            </div>
          </div>

          <div className="landing-parity__table-wrap">
            <table className="parity">
              <caption className="visually-hidden">{t('landing.parity_caption')}</caption>
              <thead>
                <tr>
                  <th scope="col">
                    <span className="visually-hidden">Capability</span>
                  </th>
                  <th scope="col">{t('landing.no_account')}</th>
                  <th scope="col">{t('landing.account')}</th>
                </tr>
              </thead>
              <tbody>
                {PARITY_CONFIG.map((row) => (
                  <tr key={row.key}>
                    <th scope="row">{t(row.labelKey)}</th>
                    <ParityCell available={row.withoutAccount} />
                    <ParityCell available={row.withAccount} />
                  </tr>
                ))}
              </tbody>
            </table>

            <p className="parity__footnote">{t('landing.parity_footnote')}</p>
          </div>
        </div>
      </section>

      {/* ── scope statement ───────────────────────────────────────────── */}
      <section className="landing-scope">
        <p>{t('landing.scope_statement')}</p>
      </section>

      {/* ── organizations ─────────────────────────────────────────────── */}
      <section className="landing-orgs" id="organizations">
        <div>
          <p className="landing-eyebrow">{t('landing.orgs_eyebrow')}</p>
          <h2 className="landing-orgs__title">{t('landing.orgs_title')}</h2>
          <p className="landing-orgs__body">{t('landing.orgs_body')}</p>

          <div className="landing-orgs__actions">
            <Link className="btn btn--plain" to="/register">
              {t('landing.request_org')}
            </Link>
            <Link className="landing-orgs__browse" to="/templates">
              {t('landing.browse_templates')}
            </Link>
          </div>
        </div>

        <aside className="landing-orgs__card">
          <p className="landing-eyebrow">{t('landing.official_templates')}</p>
          <ul>
            {PLACEHOLDER_OFFICIAL_TEMPLATES.map((template) => (
              <li key={template.name}>
                <span>{template.name}</span>
                <code>v{template.version}</code>
              </li>
            ))}
          </ul>
        </aside>
      </section>

      {/* ── call to action ────────────────────────────────────────────── */}
      <section className="landing-cta">
        <div>
          <p className="landing-cta__title">{t('landing.cta_title')}</p>
          <p className="landing-cta__note">{t('landing.cta_note')}</p>
        </div>
        <Link className="btn btn--primary" to="/liturgies/new">
          {t('landing.plan_liturgy')}
        </Link>
      </section>

      <footer className="landing-footer">
        <p className="landing-footer__motto">{t('landing.footer_motto')}</p>
        <p className="landing-footer__links">
          <span>{t('landing.privacy')}</span>
          <span>{t('landing.terms')}</span>
          <span>{t('landing.contact')}</span>
          <button
            type="button"
            style={{
              background: 'none',
              border: 'none',
              color: 'inherit',
              font: 'inherit',
              cursor: 'pointer',
              padding: 0,
              textDecoration: 'underline',
            }}
            onClick={() => void setLanguage(i18n.language.startsWith('es') ? 'en' : 'es')}
          >
            {i18n.language.startsWith('es') ? 'English' : 'Español'}
          </button>
        </p>
      </footer>
    </div>
  );
}

/**
 * A parity cell.
 *
 * The mark is `aria-hidden` and paired with visually-hidden text, so the meaning does not depend on
 * recognizing a glyph or a colour — a screen reader hears "Yes" or "Account only", and a
 * colour-blind reader is not asked to distinguish blue from grey.
 */
function ParityCell({ available }: { available: boolean }): React.JSX.Element {
  return (
    <td className={available ? 'parity__yes' : 'parity__no'}>
      <span aria-hidden="true">{available ? '✓' : '—'}</span>
      <span className="visually-hidden">{available ? 'Yes' : 'No'}</span>
    </td>
  );
}
